import React, { useState, useEffect } from 'react';
import Square from './Square';
import RoyalIndicator from './RoyalIndicator';
import '../styles/ChessBoard.css';

const ChessBoard = ({ gameState, onSquareClick, selectedSquare, legalMoves, inCheck, dukeSkinWhite, dukeSkinBlack, pieceSkinWhite, pieceSkinBlack, boardFlipped = false }) => {
  const { 
    squares, 
    white_king_index, 
    black_king_index, 
    white_royal_index,
    black_royal_index,
    white_king_captured,
    black_king_captured,
    white_embodiment, 
    black_embodiment, 
    variant,
    white_duke_restricted,
    black_duke_restricted
  } = gameState;
  
  // When king is captured, the royal indicator should be on the Duke (royal_index)
  const whiteRoyalPos = (white_king_captured && white_royal_index >= 0) ? white_royal_index : white_king_index;
  const blackRoyalPos = (black_king_captured && black_royal_index >= 0) ? black_royal_index : black_king_index;
  
  // Get duke skins from props or window global (set by SettingsModal)
  const activeDukeSkinWhite = dukeSkinWhite || (typeof window !== 'undefined' ? window.chessKingdomDukeSkinWhite : 'default') || 'default';
  const activeDukeSkinBlack = dukeSkinBlack || (typeof window !== 'undefined' ? window.chessKingdomDukeSkinBlack : 'default') || 'default';
  // Get piece skins from props or window global - separate for white and black
  const activePieceSkinWhite = pieceSkinWhite || (typeof window !== 'undefined' ? window.chessKingdomPieceSkinWhite : null) || null;
  const activePieceSkinBlack = pieceSkinBlack || (typeof window !== 'undefined' ? window.chessKingdomPieceSkinBlack : null) || null;

  const renderSquare = (index) => {
    const file = index % 8;
    const rank = Math.floor(index / 8);
    const isLight = (file + rank) % 2 !== 0;  // Fixed: a1 is now dark
    const piece = squares[index];
    const isSelected = selectedSquare === index;
    const isLegalMove = legalMoves?.some(m => m.to === index) || false;
    
    // Determine embodiment for this piece
    let embodiment;
    if (variant === 'kings_court_2dukes' && piece?.type === 'duke') {
      // For Kingdom Chess (Two Dukes), embodiment is position-based
      // c1/c8 (file 2, positions 2/58) = Fight to be King Duke
      // f1/f8 (file 5, positions 5/61) = Instant King Duke
      if (file === 2) {
        // c-file = Fight to be King Duke
        embodiment = 'fight_to_be_king';
      } else if (file === 5) {
        // f-file = Instant King Duke
        embodiment = 'instant_king';
      } else {
        // Duke has moved - use player's chosen embodiment as fallback
        embodiment = piece.side === 'white' ? white_embodiment : black_embodiment;
      }
    } else {
      // For single Duke variants, use board-level embodiment
      embodiment = piece?.side === 'white' ? white_embodiment : black_embodiment;
    }
    
    // Select the appropriate Duke skin based on piece side
    const dukeSkinForPiece = piece?.side === 'white' ? activeDukeSkinWhite : activeDukeSkinBlack;
    // Select the appropriate piece skin based on piece side
    const pieceSkinForPiece = piece?.side === 'white' ? activePieceSkinWhite : activePieceSkinBlack;
    
    return (
      <Square
        key={`sq-${index}`}
        index={index}
        isLight={isLight}
        piece={piece}
        embodiment={embodiment}
        dukeSkin={dukeSkinForPiece}
        pieceSkin={pieceSkinForPiece}
        isSelected={isSelected}
        isLegalMove={isLegalMove}
        onClick={() => onSquareClick(index)}
      />
    );
  };

  // Detect when both sides have the same dark skin (obsidian vs obsidian or kawaiiBlack vs kawaiiBlack)
  const isSameDarkSkin = 
    (activePieceSkinWhite === 'obsidian' && activePieceSkinBlack === 'obsidian') ||
    (activePieceSkinWhite === 'kawaiiBlack' && activePieceSkinBlack === 'kawaiiBlack');
  
  // State for grid settings (listen to settings changes)
  const [gridSettings, setGridSettings] = useState({
    enabled: false,
    innerGrid: true,
    outerBorder: true
  });

  // Listen for settings changes and load initial grid settings
  useEffect(() => {
    const handleSettingsChange = (event) => {
      if (event.detail?.gridSettings) {
        setGridSettings(event.detail.gridSettings);
      }
    };

    // Load initial settings - check for multiplayer host settings first
    if (window.chessKingdomGridSettings) {
      // Multiplayer: use host's grid settings
      setGridSettings(window.chessKingdomGridSettings);
    } else {
      // Solo/local: load from localStorage
      try {
        const saved = localStorage.getItem('chessKingdomSettings');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.gridSettings) {
            setGridSettings(parsed.gridSettings);
          }
        }
      } catch (e) {
      }
    }

    window.addEventListener('settingsChanged', handleSettingsChange);
    return () => window.removeEventListener('settingsChanged', handleSettingsChange);
  }, []);

  // Build class names for the chess board
  const boardClasses = [
    'chess-board',
    boardFlipped ? 'board-flipped' : '',
    isSameDarkSkin ? 'same-dark-skin-battle' : '',
    activePieceSkinWhite === 'obsidian' && activePieceSkinBlack === 'obsidian' ? 'obsidian-vs-obsidian' : '',
    activePieceSkinWhite === 'kawaiiBlack' && activePieceSkinBlack === 'kawaiiBlack' ? 'kawaiiblack-vs-kawaiiblack' : '',
    // Grid line classes based on settings
    gridSettings.enabled && gridSettings.innerGrid ? 'show-grid-inner' : '',
    gridSettings.enabled && gridSettings.outerBorder ? 'show-grid-outer' : ''
  ].filter(Boolean).join(' ');

  // Always use normal order - CSS transform handles the flip
  const squareIndices = [...Array(64)].map((_, i) => i);

  // Rank and file arrays - reversed when flipped
  const ranks = boardFlipped ? [1, 2, 3, 4, 5, 6, 7, 8] : [8, 7, 6, 5, 4, 3, 2, 1];
  const files = boardFlipped ? ['h', 'g', 'f', 'e', 'd', 'c', 'b', 'a'] : ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

  return (
    <div className="chess-board-container">
      <div className={boardClasses}>
        {squareIndices.map((index) => renderSquare(index))}
        
        {/* Coordinates inside the board squares */}
        {/* Rank numbers (8-1 or 1-8 when flipped) in top-left of a-file squares */}
        {ranks.map((rank, idx) => {
          const row = idx;
          const isLightSquare = boardFlipped ? (row % 2 !== 0) : (row % 2 === 0);
          return (
            <div 
              key={`rank-${rank}`} 
              className={`board-coordinate rank-label ${isLightSquare ? 'on-light' : 'on-dark'}`}
              style={{ 
                position: 'absolute',
                top: `${row * 12.5}%`,
                left: '2px',
                width: '12.5%',
                height: '12.5%',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                padding: '2px 0 0 3px'
              }}
            >
              {rank}
            </div>
          );
        })}
        
        {/* File letters (a-h or h-a when flipped) at bottom-center of 1st rank squares */}
        {files.map((file, idx) => {
          const col = idx;
          const isLightSquare = boardFlipped ? (col % 2 !== 0) : (col % 2 === 0);
          return (
            <div 
              key={`file-${file}`} 
              className={`board-coordinate file-label ${isLightSquare ? 'on-dark' : 'on-light'}`}
              style={{ 
                position: 'absolute',
                bottom: '0',
                left: `${col * 12.5}%`,
                width: '12.5%',
                height: '12.5%',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                padding: '0 0 2px 0'
              }}
            >
              {file}
            </div>
          );
        })}
        
        {/* Royal Indicators - Track royal piece position (King or Duke after king capture) */}
        {whiteRoyalPos >= 0 && (
          <RoyalIndicator
            position={whiteRoyalPos}
            color="white"
            inCheck={inCheck && gameState.side_to_move === 'white'}
            embodiment={white_embodiment}
            restricted={!!white_duke_restricted}
          />
        )}
        {blackRoyalPos >= 0 && (
          <RoyalIndicator
            position={blackRoyalPos}
            color="black"
            inCheck={inCheck && gameState.side_to_move === 'black'}
            embodiment={black_embodiment}
            restricted={!!black_duke_restricted}
          />
        )}
      </div>
    </div>
  );
};

export default ChessBoard;
